import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthContext } from "@/context/AuthContext";
import badgerLogo from "@/assets/image.png";

export function LoginPage() {
  const {
    error,
    sessionExpiredMessage,
    isRedirecting,
    signInWithMicrosoft,
    signInWithPassword,
    clearSessionExpiredMessage,
  } = useAuthContext();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isMsalEnabled = import.meta.env.VITE_MSAL_ENABLED === "true";

  async function handlePasswordSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    clearSessionExpiredMessage();
    try {
      await signInWithPassword(email, password);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f4f6f8",
        p: 2,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box
            component="img"
            src={badgerLogo}
            alt="Badger"
            sx={{ height: 48, mb: 1 }}
          />
          <Typography variant="h6" fontWeight={700}>
            Sign in to Badger
          </Typography>
        </Box>

        {sessionExpiredMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {sessionExpiredMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isMsalEnabled ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isRedirecting}
            onClick={signInWithMicrosoft}
            sx={{ mb: 2, textTransform: "none" }}
          >
            {isRedirecting ? "Redirecting…" : "Sign in with Microsoft"}
          </Button>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Microsoft sign-in is not configured for this environment.
          </Alert>
        )}

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            or
          </Typography>
        </Divider>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Admin sign-in (email &amp; password)
        </Typography>

        <Box
          component="form"
          onSubmit={handlePasswordSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <Button
            type="submit"
            variant="outlined"
            fullWidth
            disabled={submitting}
            sx={{ textTransform: "none" }}
          >
            {submitting ? "Signing in…" : "Sign in with email & password"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
